import { useContext, useEffect } from 'react';

import { RouteContext } from 'src/context';
import history from 'src/history';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { setActiveCompanyId } from 'src/store/activeCompany/slice';
import { UrlUtils } from 'src/utils';

/**
 * Hook to check for activeCompanyId query parameter and set it in the Redux store.
 * This should be called early in the app lifecycle to ensure client users land
 * in the correct company context when clicking email CTAs.
 *
 * After setting the activeCompanyId, the query parameter is removed from the URL
 * to prevent it from persisting in bookmarks or being shared inadvertently.
 */
export const useActiveCompanyIdFromQuery = () => {
  const dispatch = useAppDispatch();
  const { query, pathname } = useContext(RouteContext);
  const currentActiveCompanyId = useAppSelector(
    (state) => state.activeCompany.activeCompanyId,
  );

  useEffect(() => {
    const activeCompanyIdFromQuery = query.activeCompanyId as
      | string
      | undefined;

    // Only set the activeCompanyId if:
    // 1. There's an activeCompanyId in the query parameters
    // 2. It's different from the current activeCompanyId in the store
    if (
      activeCompanyIdFromQuery &&
      activeCompanyIdFromQuery !== currentActiveCompanyId
    ) {
      dispatch(setActiveCompanyId(activeCompanyIdFromQuery));

      // Clean up the URL by removing the activeCompanyId query parameter
      const cleanedQuery = { ...query };
      delete cleanedQuery.activeCompanyId;

      const queryString = UrlUtils.FormatUrlQueryToString(cleanedQuery);
      const cleanPath = queryString ? `${pathname}?${queryString}` : pathname;

      // Replace the current URL with the cleaned URL without the activeCompanyId parameter
      // Using history.replace to avoid adding to browser history and prevent back button issues
      history.replace(cleanPath);
    }
  }, [query, pathname, currentActiveCompanyId, dispatch]);
};
